import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useEmotionData } from './EmotionData';

const EmotionPieChart = ({ artworkId, embedded = false }) => {
  const { rawData, processedData, loading,error } = useEmotionData(artworkId);
  const shellClass = embedded
    ? 'w-full h-full flex items-center justify-center text-center text-white'
    : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-full text-center text-white';

  if (loading) {
    return <div className={shellClass}>Loading emotion data...</div>;
  }

  if (error) {
    return <div className={`${shellClass} text-red-400`}>{error}</div>;
  }

  console.log('Processed data:', processedData);
  if (!rawData || rawData.length === 0) {
    return <div className={`${shellClass} text-gray-300`}>No emotion data available for this artwork</div>;
  }

  // 3-color gradients for each emotion
  const getEmotionGradient = (emotion) => {
    const gradients = {
      positive: {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 1,
        colorStops: [
          { offset: 0, color: '#10b981' },   // Emerald Green
          { offset: 0.5, color: '#34d399' },  // Bright Emerald
          { offset: 1, color: '#6ee7b7' }     // Light Emerald
        ]
      },
      neutral: {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 1,
        colorStops: [
          { offset: 0, color: '#f59e0b' },   // Amber
          { offset: 0.5, color: '#fbbf24' },  // Bright Amber
          { offset: 1, color: '#fcd34d' }     // Light Amber
        ]
      },
      negative: {
        type: 'linear',
        x: 0, y: 0, x2: 1, y2: 1,
        colorStops: [
          { offset: 0, color: '#ef4444' },   // Red
          { offset: 0.5, color: '#f87171' },  // Bright Red
          { offset: 1, color: '#fca5a5' }     // Light Red
        ]
      }
    };
    return gradients[emotion] || '#9E9E9E';
  };

  const emotionGlowColors = {
    positive: "rgba(16, 185, 129, 0.7)",    // Emerald Green glow
    neutral: "rgba(245, 158, 11, 0.7)",     // Amber glow
    negative: "rgba(239, 68, 68, 0.7)"      // Red glow
  };

  const option = {
    title: {
      text: 'Emotion Distribution',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'item',
      appendToBody: embedded,
      formatter: '{b}: {c} ({d}%)',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15]
    },
    legend: {
      orient: 'horizontal',
      bottom: '15%',
      left: 'center',
      itemGap: 10,
      itemWidth: 14,
      itemHeight: 12,
      itemBorderWidth: 1,
      itemBorderColor: '#e5e5e5',
      // padding: [4, 8],
      textStyle: {
        fontSize: 12,
        fontWeight: 'normal',
        color: '#e0e0e0'
      }
    },
    series: [
      {
        name: 'Emotions',
        type: 'pie',
        radius: ['30%', '50%'],
        center: ['50%', '35%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#EEE',
          borderWidth: 1,
          shadowBlur: 20,
          shadowOffsetX: 0,
          shadowOffsetY: 0
        },
        label: {
          show: true,
          formatter: '{b}\n({c})',
          fontSize: 13,
          fontWeight: 'bold',
          color: '#ffffff'
        },
        labelLine: {
          length: 20,
          length2: 15,
          lineStyle: {
            width: 2
          }
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 16,
            fontWeight: 'bold'
          },
          itemStyle: {
            shadowBlur: 35,
            shadowColor: 'rgba(255, 255, 255, 0.9)',
            borderWidth: 0
          },
          scale: true,
          scaleSize: 4
        },
          data: processedData.barChart.map(item => ({
          value: item.count,
          name: item.emotion.charAt(0).toUpperCase() + item.emotion.slice(1),
          itemStyle: {
            color: getEmotionGradient(item.emotion),
            shadowBlur: 18,
            shadowColor: emotionGlowColors[item.emotion] || "rgba(158, 158, 158, 0.7)",
            shadowOffsetX: 0,
            shadowOffsetY: 0
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 35,
              shadowColor: emotionGlowColors[item.emotion] || "rgba(158, 158, 158, 0.9)"
            }
          }
        }))
      }
    ],
    animationType: 'scale',
    animationEasing: 'elasticOut',
    animationDelay: function (idx) {
      return Math.random() * 200;
    }
  };

  return (
    <div className={embedded ? 'w-full h-full' : 'w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800'}>
      <div className={embedded ? 'h-full' : 'h-80'}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default EmotionPieChart;